import { yupResolver } from '@hookform/resolvers/yup'
import { EJobType, EOtherIncomeType, getEJobTypeList, getEOtherIncomeTypeSelectableList } from '@if/core/types'
import { InputText, ScrollableAlert, SelectInput } from '@src/components'
import PhoneExtInput from '@src/components/PhoneExtInput'
import PhoneInput from '@src/components/PhoneInput'
import { useTranslatedOptionEnum } from '@src/components/SelectInput'
import { Constants, translateErrorCode } from '@src/types'
import { range } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { calculateTotalMonths, helpTip } from '../../../services/utils'
import { EmploymentInput, EmploymentInputSchema } from './PrequalificationSchema'
import { FORM_ID } from './StepperFormFooter'

interface Props {
  applicantData: EmploymentInput
  onApplicantUpdated: (data: EmploymentInput) => void
  useSelectForYearsOfResidenceAndEmployment: boolean
}

const EmploymentForm = ({ onApplicantUpdated, applicantData, useSelectForYearsOfResidenceAndEmployment }: Props) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [displayMinIncomeMessage, setDisplayMinIncomeMessage] = useState(false)
  const [displayMaxIncomeMessage, setDisplayMaxIncomeMessage] = useState(false)
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<EmploymentInput>({
    mode: 'onBlur',
    resolver: yupResolver(EmploymentInputSchema),
    defaultValues: applicantData,
  })

  const watchOtherIncomeTypes = watch('otherIncomes.0.typeId')
  const jobType = watch('currentJobs.0.jobType')
  const currentJobSalary = watch('currentJobs.0.annualSalary')
  const otherIncomeAnnualSalary = watch('otherIncomes.0.annualAmount')
  const watchYears = watch('currentJobs.0.years')
  const watchYearMonths = watch('currentJobs.0.yearMonths')

  const onSubmit = (payload: EmploymentInput) => {
    if (
      Number(currentJobSalary ?? 0) + Number(otherIncomeAnnualSalary ?? 0) >= Constants.MinIncomes &&
      Number(currentJobSalary ?? 0) <= Constants.MaxIncomes &&
      Number(otherIncomeAnnualSalary ?? 0) <= Constants.MaxIncomes
    ) {
      onApplicantUpdated(payload)
    }
    if (
      Number(currentJobSalary ?? 0) > Constants.MaxIncomes ||
      Number(otherIncomeAnnualSalary ?? 0) > Constants.MaxIncomes
    ) {
      setDisplayMaxIncomeMessage(true)
    } else {
      setDisplayMinIncomeMessage(true)
    }
  }

  useEffect(() => {
    const years = +(watchYears ?? 0)
    const months = +(watchYearMonths ?? 0)
    const total = calculateTotalMonths(years, months, useSelectForYearsOfResidenceAndEmployment)

    setValue('currentJobs.0.months', total)
  }, [setValue, useSelectForYearsOfResidenceAndEmployment, watchYearMonths, watchYears])

  useEffect(() => {
    helpTip()
  }, [applicantData])

  useEffect(() => {
    if ((Number(jobType) as EJobType) === EJobType.Unemployed) {
      setValue('currentJobs.0.annualSalary', null, { shouldValidate: true })
    }
  }, [jobType, setValue])
  useEffect(() => {
    setValue('otherIncomes.0.jobType', jobType)
  }, [jobType, setValue])

  useEffect(() => {
    if (!watchOtherIncomeTypes) {
      setValue('otherIncomes.0.annualAmount', null)
    }
  }, [setValue, watchOtherIncomeTypes])

  const jobTypeOptions = useTranslatedOptionEnum('EJobType', getEJobTypeList(), 'enum')
  const otherIncomeTypesOptions = useTranslatedOptionEnum(
    'EOtherIncomeType',
    getEOtherIncomeTypeSelectableList(),
    'enum',
  )

  return (
    <section className="step-content">
      <h3 className="form-question">{t('income.howMuch')}</h3>

      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID} noValidate>
        <ScrollableAlert
          showAlert={displayMinIncomeMessage || displayMaxIncomeMessage}
          type="warn"
          message={displayMinIncomeMessage ? t('income.minAnnualSalary') : t('income.maxAnnualSalary')}
        />
        <SelectInput
          id="employment_category"
          label="income.category"
          error={errors.currentJobs?.[0]?.jobType}
          hasEmptyValue
          disableEmptyValue
          defaultValue=""
          options={jobTypeOptions}
          {...register('currentJobs.0.jobType')}
        />

        {(Number(jobType) as EJobType) !== EJobType.Unemployed && (
          <>
            <div className={`control-group  ${errors.currentJobs?.[0]?.annualSalary && 'error'}`}>
              <label htmlFor="gross_anual">
                {t('income.beforeDeductions')}
                <span className="help-tip">
                  <p>{t('income.grossAnnualInfo')}</p>
                </span>
              </label>
              <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
                <InputText
                  type="number"
                  title={t('income.grossAnnualInfo')}
                  id="annualSalary"
                  error={errors.currentJobs?.[0]?.annualSalary}
                  {...register('currentJobs.0.annualSalary')}
                />
              </div>
            </div>
            <div className={`control-group ${errors.currentJobs?.[0]?.jobTitle && 'error'}`}>
              <label htmlFor="job_title">{t('income.jobTitle')}</label>
              <input type="text" id="jobTitle" {...register('currentJobs.0.jobTitle')} maxLength={50} />
            </div>

            <div className="control-group-wrap" style={{ alignItems: 'start' }}>
              <div className={`control-group mid ${errors.currentJobs?.[0]?.employerName && 'error'}`}>
                <label htmlFor="employer_name">{t('income.employerName')}</label>
                <input type="text" id="employerName" {...register('currentJobs.0.employerName')} maxLength={100} />
              </div>

              <PhoneInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhone"
                label={t('income.phoneNumber')}
              />
              <PhoneExtInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhoneExt"
                label={t('income.extension')}
              />
            </div>

            <div className="control-group mid">
              <label htmlFor="employment_years">{t('income.employmentLenght')}</label>
              <div
                className={
                  useSelectForYearsOfResidenceAndEmployment
                    ? `dropdown-wrap ${errors.currentJobs?.[0]?.months ? 'error' : ''}`
                    : 'dropdown-wrap'
                }
              >
                <select id="year" {...register('currentJobs.0.years')}>
                  <option value={0}>{t('common.years')}</option>
                  {useSelectForYearsOfResidenceAndEmployment && (
                    <option value={-1}>{t('common.lessThanOneYear')}</option>
                  )}
                  {range(1, 15).map((item) => (
                    <option value={item} key={item}>
                      {t('common.yearsLabel', { count: item })}
                    </option>
                  ))}
                  <option value={15}>
                    {t('common.moreThan')} {t('common.yearsLabel', { count: 15 })}
                  </option>
                </select>
              </div>
            </div>
            {!useSelectForYearsOfResidenceAndEmployment && (
              <div className="control-group mid">
                <div className={`dropdown-wrap ${errors.currentJobs?.[0]?.months && 'error'}`}>
                  <select id="month" {...register('currentJobs.0.yearMonths')}>
                    <option value={0}>{t('common.months')}</option>
                    {range(1, 12).map((item) => (
                      <option value={item} key={item}>
                        {t('common.monthsLabel', { count: item })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}

        <div className="control-group">
          <h1 className="h3">
            {t('income.otherIncome')}
            <span className="help-tip">
              <p>{t('income.otherIncomeInfo')}</p>
            </span>
          </h1>
        </div>
        <div className="control-group mid">
          <label htmlFor="otherIncomeType">{t('income.otherIncome')}</label>
          <div className={`dropdown-wrap ${errors.otherIncomes?.[0]?.typeId && 'error'}`}>
            <select id="otherIncomeType" {...register('otherIncomes.0.typeId')}>
              <option aria-label={t('common.none')}> </option>
              {otherIncomeTypesOptions}
            </select>
          </div>
        </div>

        <div className={`control-group mid ${errors.otherIncomes?.[0]?.annualAmount && 'error'}`}>
          <label htmlFor="other_source">{t('income.annualIncome')}</label>
          <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
            <input
              type="number"
              id="annualAmount"
              disabled={!watchOtherIncomeTypes}
              {...register('otherIncomes.0.annualAmount')}
            />
            {errors.otherIncomes?.[0]?.annualAmount && (
              <p className="error-message" style={{ position: 'absolute' }}>
                {translateErrorCode(errors.otherIncomes?.[0]?.annualAmount, { context: 'annualAmount' })}
              </p>
            )}
          </div>
        </div>

        {(Number(watchOtherIncomeTypes) as EOtherIncomeType) === EOtherIncomeType.Other && (
          <div className={`control-group ${errors.otherIncomes?.[0]?.description && 'error'}`}>
            <label htmlFor="other_source">{t('income.otherIncomeDescription')}</label>
            <input type="text" id="description" {...register('otherIncomes.0.description')} maxLength={50} />
          </div>
        )}
      </form>
    </section>
  )
}

export default EmploymentForm
